import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api, itemImage, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Layers2, ChevronUp, Link2, Unlink, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Items that share a set_id, as grid entries: { kind: "set", key, id, name, items }. */
export const groupSets = (list) => {
  const sets = new Map();
  const rest = [];
  for (const it of list) {
    if (!it.set_id) { rest.push(it); continue; }
    if (!sets.has(it.set_id)) sets.set(it.set_id, { kind: "set", key: `set:${it.set_id}`, id: it.set_id, name: it.set_name || "Suit set", items: [] });
    sets.get(it.set_id).items.push(it);
  }
  // order pieces jacket -> vest -> trousers
  const rank = (i) => (i.category === "bottom" ? 2 : i.category === "outerwear" ? 0 : 1);
  for (const s of sets.values()) s.items.sort((a, b) => rank(a) - rank(b));
  return { sets: [...sets.values()], rest };
};

/** Task 6 - one card for a linked jacket + trousers (+ vest). Tap shows the pieces. */
export const SetCard = ({ set, onExpand }) => {
  const worth = set.items.reduce((s, i) => s + ((i.worth_override ?? i.estimated_worth) || 0), 0);
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="group relative text-left rounded-2xl bg-card lift" data-testid={`set-card-${set.id}`}>
      <button onClick={onExpand} className="block w-full text-left press focus-visible:ring-0" aria-label={`Show the pieces of ${set.name}`} data-testid="set-card-expand">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-white border border-border">
          <div className={`grid h-full ${set.items.length === 3 ? "grid-cols-3" : "grid-cols-2"} gap-px bg-border`}>
            {set.items.map((it) => <div key={it.id} className="bg-white"><img src={itemImage(it)} alt={it.name || "piece"} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 ease-spring group-hover:scale-[1.04]" /></div>)}
          </div>
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-foreground text-background px-2 h-6 text-[10px] font-semibold" data-testid="set-card-badge"><Layers2 className="h-3 w-3" /> Set</span>
          <span className="absolute bottom-2 left-2 rounded-full bg-background/90 backdrop-blur px-2 h-6 inline-flex items-center text-[10px] font-medium">{set.items.length} pieces</span>
        </div>
        <div className="px-1 pt-2.5 pb-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold leading-tight" data-testid="set-card-name">{set.name}</p>
              <p className="truncate text-[11px] text-muted-foreground mt-0.5">{set.items.map((i) => i.name).join(" + ")}</p>
            </div>
            {worth > 0 && <p className="shrink-0 text-[13px] font-mono-num font-semibold">{money(worth)}</p>}
          </div>
        </div>
      </button>
    </motion.div>
  );
};

export const SetHeader = ({ set, onCollapse, onDissolve }) => (
  <div className="col-span-full flex items-center justify-between gap-3 rounded-xl bg-secondary/70 px-3 py-2 mt-1" data-testid={`set-expanded-${set.id}`}>
    <div className="flex items-center gap-2 min-w-0">
      <Layers2 className="h-4 w-4 text-muted-foreground shrink-0" />
      <p className="text-sm font-medium truncate">Set · {set.name} <span className="text-muted-foreground font-normal">· {set.items.length} pieces, each usable on its own</span></p>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <button onClick={onDissolve} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-background transition-colors" data-testid="set-unlink-all"><Unlink className="h-3.5 w-3.5" /> Unlink set</button>
      <button onClick={onCollapse} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-background transition-colors" data-testid="set-collapse"><ChevronUp className="h-3.5 w-3.5" /> Collapse</button>
    </div>
  </div>
);

const ROLE_OF = (it) => (it.category === "bottom" ? "trousers" : it.category === "outerwear" ? "jacket" : it.category === "top" ? "jacket" : null);

/** Pick the other pieces to link with `anchor`: the role it doesn't fill is required, a vest is optional. */
export const LinkSetDialog = ({ anchor, items, open, onOpenChange, onLinked }) => {
  const [jacket, setJacket] = useState(null);
  const [trousers, setTrousers] = useState(null);
  const [vest, setVest] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const anchorRole = anchor ? ROLE_OF(anchor) : null;
  const free = useMemo(() => (items || []).filter((i) => i.id !== anchor?.id && !i.set_id && i.listing_type !== "sold"), [items, anchor]);
  const jackets = free.filter((i) => i.category === "outerwear" || i.category === "top");
  const bottoms = free.filter((i) => i.category === "bottom");
  const needJacket = anchorRole !== "jacket";
  const needTrousers = anchorRole !== "trousers";
  const canSave = (!needJacket || jacket) && (!needTrousers || trousers);

  const save = async () => {
    const ids = [anchor.id, jacket?.id, trousers?.id, vest?.id].filter(Boolean);
    setBusy(true);
    try {
      const s = await api.createSet(ids, name.trim() || undefined);
      toast.success(`Linked as ${s.name}`);
      onLinked?.(s);
      onOpenChange(false);
      setJacket(null); setTrousers(null); setVest(null); setName("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't link the set"); }
    finally { setBusy(false); }
  };

  const Picker = ({ label, list, value, onPick, optional, testid }) => (
    <div>
      <p className="text-xs text-muted-foreground mb-1.5">{label}{optional && <span className="opacity-70"> · optional</span>}</p>
      {list.length === 0 ? <p className="text-xs text-muted-foreground italic">Nothing suitable in your closet yet.</p> : (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={testid}>
          {list.map((it) => (
            <button key={it.id} onClick={() => onPick(value?.id === it.id ? null : it)} className={`shrink-0 w-[88px] text-left rounded-xl border p-1 transition-colors ${value?.id === it.id ? "border-foreground ring-1 ring-foreground" : "border-border hover:border-foreground/40"}`} aria-pressed={value?.id === it.id} data-testid={`${testid}-${it.id}`}>
              <div className="aspect-[4/5] rounded-lg overflow-hidden bg-white"><img src={itemImage(it)} alt={it.name} className="h-full w-full object-contain" /></div>
              <p className="text-[10px] font-medium truncate mt-1">{it.name}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (!anchor) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="link-set-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Link to a set</DialogTitle>
          <DialogDescription>Pair <b>{anchor.name}</b> with its matching pieces. Each piece stays its own item and can still be worn alone.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {needJacket && <Picker label="Jacket" list={jackets.filter((i) => i.id !== vest?.id)} value={jacket} onPick={setJacket} testid="link-set-jackets" />}
          {needTrousers && <Picker label="Trousers" list={bottoms} value={trousers} onPick={setTrousers} testid="link-set-trousers" />}
          <Picker label="Vest" list={jackets.filter((i) => i.id !== jacket?.id)} value={vest} onPick={setVest} optional testid="link-set-vests" />
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Set name <span className="opacity-70">· optional</span></p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Navy wedding suit" className="h-9" data-testid="link-set-name" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={!canSave || busy} className="rounded-full" data-testid="link-set-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Link as set</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { Link2 as LinkIcon, Unlink as UnlinkIcon, Check as CheckIcon };
